/**
 * Custom hook to get the organization user data for a specific user and organization.
 *
 * @author Rammetec
 * @version 1.0.0
 * @since 2025-01-27
 */
import { useQuery } from '@tanstack/react-query';
import { organizationService } from '@syscolect/domain';
import { useDispatchHookEvent } from '@rammetec/core';
import { useCurrentUser } from './useCurrentUser.js';
import { useSessionManager } from './useSessionManager.js';
/**
 * Custom hook to get the organization user data for a specific user and organization.
 * If userId and orgId are not provided, it will use the current user and current organization from session.
 *
 * Hook that fetches the current user's membership data within an organization (role, currentProfile, etc.).
 *
 * @param {string | undefined} userId - The user ID (optional, defaults to current user)
 * @param {string | undefined} orgId - The organization ID (optional, defaults to current organization)
 * @returns {Object} - Object containing the organization user data, loading state, and error.
 */
export const useCurrentOrgUserData = (userId, orgId) => {
    // Get defaults from session/user hooks if not provided
    const { currentUserId, isLoading: isUserLoading } = useCurrentUser();
    const { currentOrganizationId, isLoading: isSessionLoading } = useSessionManager();
    // Determine effective IDs
    const effectiveUserId = userId ?? currentUserId;
    const effectiveOrgId = orgId ?? currentOrganizationId;
    // Query to fetch the user's data within the specific organization
    const { data: orgUserData, isLoading: isOrgUserLoading, error: orgUserError } = useQuery({
        queryKey: ['orgUser', effectiveOrgId, effectiveUserId],
        queryFn: () => {
            if (!effectiveOrgId || !effectiveUserId) {
                return null;
            }
            return organizationService.getOrganizationUser(effectiveOrgId, effectiveUserId);
        },
        enabled: !!effectiveOrgId && !!effectiveUserId,
    });
    // Calculate composite loading state
    // If specific IDs were provided, we don't care about session loading state
    const isLoading = isOrgUserLoading ||
        (userId === undefined ? isUserLoading : false) ||
        (orgId === undefined ? isSessionLoading : false);
    useDispatchHookEvent('useCurrentOrgUserData', {
        orgUserData,
        isLoading,
        error: orgUserError,
        effectiveUserId,
        effectiveOrgId
    });
    return {
        orgUserData,
        isLoading,
        error: orgUserError,
    };
};
//# sourceMappingURL=useCurrentOrgUserData.js.map